import {
  EPDDetailIN,
  EPDDetailIndicator,
  EPDDetailModuleValue,
  EPDFieldNameEnum,
  ParsedIndicators,
  ReferenceUnit,
  getLocalAuthHeader,
  inventoryEPDsApi,
  manufacturerApi,
  materialApi,
  searchAPI,
} from "api-client";
import { BGItem } from "components/ButtonGroups";
import { SelectOption } from "components/SimpleSelect";
import { epdSearchStore } from "features/EPDSearchModal/EPDSearchStore";
import { REFERENCE_UNITS } from "features/MappingEditor/consts";
import { makeAutoObservable } from "mobx";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { MODULES_LIST } from "./consts";
import { CONFORMITY_LIST } from "./features/EnvironmentalIndicators";
import moment from "moment";
import { BPEStore } from "features/BuildingProductEditor/ProductEditorStore";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";

interface FilterOptions {
  [key: string]: SelectOption[];
}

export interface IndicatorsProps {
  [key: string]: number | string;
}

export interface CustomEPD {
  id: string;
  name?: string;
  data_type?: string | null;
  mass_conversion_factor?: number;
  mass_value_m?: number;
  mass_value_m2?: number;
  mass_value_m3?: number;
  conformity_name: string;
  reference_factor?: number;
  reference_unit?: string;
  default_material_name?: string;
  manufacturer?: string;
  indicators?: IndicatorsProps[];
  version?: number;
  publishing_date?: string;
  expiry_year?: string;
  registration_number?: string;
  files: FileProps[];
}

const initialData: CustomEPD = {
  id: "",
  name: "",
  reference_unit: REFERENCE_UNITS[0].id,
  reference_factor: 1,
  conformity_name: CONFORMITY_LIST[0].id,
  publishing_date: "",
  expiry_year: "",
  files: [],
};

interface FileProps {
  file_id: string;
  file_name: string;
  url: string;
  created?: string;
}

export interface ImportedIndicators extends ParsedIndicators {
  conformity_name: string;
}

class EPDEditorStore {
  filterOptions: FilterOptions = {
    [EPDFieldNameEnum.DataType]: [],
    [EPDFieldNameEnum.Database]: [],
    [EPDFieldNameEnum.ConformityName]: [],
    material: [],
  };
  data: CustomEPD = initialData;
  filesToRemove: string[] = [];
  filesToUpload: File[] = [];
  saveValidationErrors = true;
  selectedFilesIDs: string[] = [];
  importedIndicators: ImportedIndicators | undefined;

  setFilterOptions(filterOptions: FilterOptions) {
    this.filterOptions = filterOptions;
  }

  setFilesToRemove(filesToRemove: string[]) {
    this.filesToRemove = filesToRemove;
  }

  setFilesToUpload(filesToUpload: File[]) {
    this.filesToUpload = filesToUpload;
  }

  setSaveValidationErrors(saveValidationErrors: boolean) {
    this.saveValidationErrors = saveValidationErrors;
  }

  setSelectedFilesIDs(uploadedFilesIDs: string[]) {
    this.selectedFilesIDs = uploadedFilesIDs;
  }

  setImportedIndicators(importedIndicators: ImportedIndicators | undefined) {
    this.importedIndicators = importedIndicators;
  }

  private async removeFiles() {
    if (this.filesToRemove.length > 0) {
      await Promise.all(
        this.filesToRemove.map(async (fileID) => {
          await BPEStore.deleteFile(fileID);
        })
      );
    }
    const filteredFiles = this.data.files.filter(
      (item) => !this.filesToRemove.includes(item.file_id)
    );
    this.onChangeData("files", filteredFiles);
  }

  onChangeData(
    name: string,
    value:
      | string
      | boolean
      | []
      | undefined
      | BGItem
      | Date
      | IndicatorsProps[]
      | FileProps[]
  ) {
    if (name) this.data[name as keyof CustomEPD] = value as never;
    this.checkSaveValidation();
  }

  setData(data: CustomEPD) {
    this.data = data;
  }

  async getMaterials() {
    const authHeader = getLocalAuthHeader();
    return await searchAPI
      .inventoryApiV1RoutersSearchEngineMaterials(authHeader)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        console.log("getMaterials.error", error);
      });
  }

  getFilterOptions = async () => {
    const keys = [EPDFieldNameEnum.Database, EPDFieldNameEnum.ConformityName];
    keys.map(async (key) => {
      await epdSearchStore
        .fetchConcoreEPDFilterChildren(key, "", 1, 20)
        .then((options) => {
          const optionsList = options?.items?.map((rec) => ({
            id: rec,
            name: rec,
            name_de: rec,
            value: rec,
          }));
          optionsList &&
            this.setFilterOptions({
              ...this.filterOptions,
              [key]: optionsList,
            });
        })
        .catch((err) => console.error(err, "fetchConcoreEPDFilterChildren"));
    });

    await this.getMaterials()
      .then((options) => {
        const materials = options?.map((rec) => ({
          id: rec.id,
          name: rec.name,
          name_de: rec.name,
          value: rec.id,
        }));
        materials &&
          this.setFilterOptions({
            ...this.filterOptions,
            material: materials as [],
          });
      })
      .catch((err) => console.error(err, "getMaterials"));
  };

  prepareData(data: CustomEPD) {
    return {
      source: "",
      name_de: data.name,
      name_en: data.name,
      manufacturer: data.manufacturer,
      publishing_date: data.publishing_date
        ? moment(data.publishing_date)
        : null,
      expiry_year: data.expiry_year
        ? Number(moment(data.expiry_year).format("YYYY"))
        : null,
      data_type: data.data_type,
      registration_number: data.registration_number,
      version: data.version ?? "1",
      concular_material_name_de: data.default_material_name,
      reference_unit: data.reference_unit as ReferenceUnit,
      reference_factor: data.reference_factor,
      mass_conversion_factor: data.mass_conversion_factor,
      mass_value_m: data.mass_value_m,
      mass_value_m2: data.mass_value_m2,
      mass_value_m3: data.mass_value_m3,
      conformity_name: data.conformity_name,
      indicators: this.prepareIndicators(data.indicators),
      file_ids: [
        ...(this.data.files ?? [])?.map((item) => item.file_id),
        ...this.selectedFilesIDs,
      ],
    } as EPDDetailIN;
  }

  private prepareIndicators(indicatorsList: IndicatorsProps[] | undefined) {
    const indicators = indicatorsList?.map((rec) => ({
      indicator_name: String(rec.indicator),
      module_values: MODULES_LIST.map((module) => ({
        module_name: String(module),
        value: Number(rec[module]),
      })),
    }));

    return indicators;
  }

  checkIndicatorsValidation() {
    const hasValues = (rec: IndicatorsProps) =>
      ["A1", "A2", "A3", "A1-A3"].some((key) => Number(rec[key]));

    const validateIndicator = (indicatorName: string) =>
      this.data.indicators?.some(
        (rec) => rec.indicator === indicatorName && hasValues(rec)
      );

    const GWPValidation = validateIndicator("GWP");
    const PENRTValidation = validateIndicator("PENRT");

    const A1Validity = Boolean(GWPValidation && PENRTValidation);
    const A2Validity = Boolean(GWPValidation && PENRTValidation);

    return this.data.conformity_name === "A1" ? A1Validity : A2Validity;
  }

  checkSaveValidation() {
    const validity =
      this.data.name &&
      this.data.manufacturer &&
      this.data.publishing_date &&
      this.data.expiry_year &&
      this.data.data_type &&
      this.data.reference_unit &&
      this.data.reference_factor &&
      this.checkIndicatorsValidation();
    this.setSaveValidationErrors(!validity);
  }

  async editEPD(org_id?: string) {
    if (this.saveValidationErrors) return;
    const orgID = org_id;
    if (!orgID) return;
    await this.removeFiles();
    const props = this.prepareData(this.data);

    await inventoryEPDsApi()
      .inventoryApiV1RoutersEpdEditEpd(orgID, this.data.id, props)
      .catch((error) => console.error(error, "EditEpd"));
  }

  async createEPD(org_id?: string) {
    if (this.saveValidationErrors) return;
    const orgID = org_id ?? sidebarMenuStore.currentOrganisation?.id;
    if (!orgID) return;
    await inventoryEPDsApi().inventoryApiV1RoutersEpdCreateEpd(
      orgID,
      this.prepareData(this.data)
    );
  }

  async fetchManufacturers(page: number, pageSize: number, searchTerm: string) {
    return await manufacturerApi()
      .buildingProductsApiV1RoutersManufacturerSearchManufacturer(
        searchTerm,
        page,
        pageSize
      )
      .then((response) => response.data);
  }

  async createManufacturer(name: string) {
    return await manufacturerApi()
      .buildingProductsApiV1RoutersManufacturerCreateManufacturer({
        name: name,
      })
      .then((response) => response.data)
      .catch((error) => console.error(error, "CreateManufacturer"));
  }

  async fetchMaterials(search_term: string, category_name?: string) {
    return await materialApi()
      .buildingProductsApiV1RoutersMaterialSearchMaterial(
        search_term,
        category_name
      )
      .then((response) => {
        return response.data.map((rec) => ({
          id: rec.category_name,
          name: rec.category_name,
          children: rec.children?.map((child) => ({
            id: child.id,
            name: child.name as string,
            category: rec.category_name,
          })),
        }));
      });
  }

  async fetchIndicators() {
    return await inventoryEPDsApi()
      .inventoryApiV1RoutersEpdGetEpdIndicatorsForConformity(
        this.data.conformity_name
      )
      .then((response) => response.data)
      .catch((error) => console.error(error, "GetEpdIndicatorsForConformity"));
  }

  async fetchIndicatorsFromFile(conformityName: string, file: File) {
    return await inventoryEPDsApi()
      .inventoryApiV1RoutersEpdGetIndicatorsFromFile(conformityName, file)
      .then((response) => response.data);
  }

  async fetchIndicatorsWithAI(conformityName: string, files: File[]) {
    return await inventoryEPDsApi()
      .inventoryApiV1RoutersEpdParseIndicatorsWithAi(conformityName, files)
      .then((response) => response.data);
  }

  restructureIndicators(indicators: EPDDetailIndicator[]) {
    return indicators.map((rec) => {
      const values = rec.module_values.map((module: EPDDetailModuleValue) => ({
        [module.module_name as string]: String(module.value),
      }));
      return Object.assign(
        { indicator: rec.indicator_name, unit: rec.unit },
        ...values
      );
    });
  }

  async fetchCSVTemplate(conformityName: string) {
    const authHeader = getLocalAuthHeader();
    return await inventoryEPDsApi()
      .inventoryApiV1RoutersEpdGetEpdUploadCsvTemplate(conformityName, {
        ...authHeader,
        responseType: "blob",
      })
      .then((response) => {
        response && this.downloadFile(response.data as unknown as string);
      });
  }

  downloadFile(data: string) {
    try {
      // Convert string to a Blob
      const blob = new Blob([data], { type: "application/csv" });
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error("Invalid Blob data");
      }
      // Create a URL for the Blob
      const url = window.URL.createObjectURL(blob);
      // Create an anchor element to trigger the download
      const a = document.createElement("a");
      a.href = url;
      a.download = "csv_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error handling CSV download:", error);
    }
  }

  reset() {
    this.data = initialData;
    this.filesToUpload = [];
    this.filesToRemove = [];
    this.saveValidationErrors = true;
    this.selectedFilesIDs = [];
    this.importedIndicators = undefined;
  }

  constructor() {
    makeAutoObservable(this);
  }

  static instance: EPDEditorStore;
  static getInstance(): EPDEditorStore {
    if (!EPDEditorStore.instance) {
      EPDEditorStore.instance = new EPDEditorStore();
    }
    return EPDEditorStore.instance;
  }
}

export const epdEditorStore = EPDEditorStore.getInstance();
